import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, Trash2, Type } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import {
  BUILTIN_FONTS,
  FONT_ASSIGNMENTS,
  customFontFamilyValue,
} from '../../utils/siteFonts';

const FontSettings = ({ settings, onSettingsChange }) => {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchFonts = useCallback(async () => {
    try {
      const res = await axios.get(API_ENDPOINTS.GET_FONTS);
      if (res.data?.success) setFonts(res.data.fonts || []);
    } catch (e) {
      console.error('Failed to load fonts', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFonts();
  }, [fetchFonts]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('font', file);
    setUploading(true);
    try {
      const res = await axios.post(API_ENDPOINTS.UPLOAD_FONT, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.success) {
        await fetchFonts();
        alert('Font uploaded successfully.');
      } else {
        alert(res.data?.message || 'Upload failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fontId) => {
    if (!window.confirm('Delete this font? Pages using it will fall back to defaults.')) return;
    setDeletingId(fontId);
    try {
      const res = await axios.post(API_ENDPOINTS.DELETE_FONT, { font_id: fontId });
      if (res.data?.success) {
        await fetchFonts();
      } else {
        alert(res.data?.message || 'Delete failed');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const fontOptions = [
    ...BUILTIN_FONTS.map((f) => ({ value: f.value, label: f.label, group: 'Built-in' })),
    ...fonts.map((f) => ({
      value: customFontFamilyValue(f),
      label: `${f.font_family} (uploaded)`,
      group: 'Uploaded',
    })),
  ];

  return (
    <div className="font-settings">
      <h2 className="settings-section-title">Typography &amp; fonts</h2>
      <p className="settings-section-hint">
        Upload custom font files (WOFF, WOFF2, TTF, OTF — max 2MB) and assign them to each part of the public website.
        Google fonts in the list load automatically; uploaded fonts are served from your server.
      </p>

      <div className="font-upload-row">
        <label className="btn-secondary font-upload-btn">
          <Upload size={18} />
          {uploading ? 'Uploading…' : 'Upload font file'}
          <input
            type="file"
            accept=".woff,.woff2,.ttf,.otf,font/woff,font/woff2,font/ttf,font/otf"
            onChange={handleUpload}
            disabled={uploading}
            hidden
          />
        </label>
      </div>

      {loading ? (
        <p className="font-settings-muted">Loading fonts…</p>
      ) : fonts.length > 0 ? (
        <ul className="font-library-list">
          {fonts.map((font) => (
            <li key={font.id} className="font-library-item">
              <div className="font-library-preview" style={{ fontFamily: customFontFamilyValue(font) }}>
                <Type size={18} />
                <span>Aa Bb Cc — {font.font_family}</span>
              </div>
              <div className="font-library-meta">
                <span>{font.original_name}</span>
                <span>{(font.file_size / 1024).toFixed(1)} KB · {String(font.file_format).toUpperCase()}</span>
              </div>
              <button
                type="button"
                className="btn-icon-danger"
                onClick={() => handleDelete(font.id)}
                disabled={deletingId === font.id}
                title="Delete font"
              >
                <Trash2 size={16} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="font-settings-muted">No uploaded fonts yet. Use the button above to add one.</p>
      )}

      <h3 className="font-assignments-title">Assign fonts to the public site</h3>
      <div className="font-assignments-grid">
        {FONT_ASSIGNMENTS.map(({ key, label, default: fallback }) => (
          <div key={key} className="form-group font-assignment-field">
            <label>{label}</label>
            <select
              value={settings[key] || fallback}
              onChange={(e) => onSettingsChange(key, e.target.value)}
            >
              {['Built-in', 'Uploaded'].map((group) => {
                const opts = fontOptions.filter((o) => o.group === group);
                if (!opts.length) return null;
                return (
                  <optgroup key={group} label={group}>
                    {opts.map((opt) => (
                      <option key={`${group}-${opt.value}`} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            <span
              className="font-assignment-preview"
              style={{ fontFamily: settings[key] || fallback }}
            >
              Preview — The quick brown fox
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FontSettings;
